import { AdminOrderResponse, InvoiceCommercialResponse, InvoiceResponse, OrderItemResponse, PaymentScheduleResponse, UserOrderResponse } from "@/api";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OrderAdminService } from "../service/orderService";
import { AdminOrderUpdateService } from "../service/adminOrderUpdateServices";
import AdminOrderForm from "./AdminOrderForm";

interface PageMessageProps {
  children: React.ReactNode;
  variant?: "normal" | "error";
}

function PageMessage({
  children,
  variant = "normal",
}: PageMessageProps) {
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-6">
      <div
        className={
          variant === "error"
            ? "rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700"
            : "rounded-xl border bg-white p-8 text-sm text-gray-500"
        }
      >
        {children}
      </div>
    </div>
  );
}


export default function AdminOrderEdit() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const parsedOrderId = Number(orderId);

  const [data, setData] =
    useState<AdminOrderResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(parsedOrderId)) {
      setError("Invalid order ID.");
      setLoading(false);
      return;
    }

    async function load() {
      try {
        const response =
          await OrderAdminService.get(parsedOrderId);

        setData(response);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Unable to load order."
        );
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [parsedOrderId]);

  if (loading) {
    return <PageMessage>Loading order...</PageMessage>;
  }

  if (error) {
    return (
      <PageMessage variant="error">
        {error}
      </PageMessage>
    );
  }

  if (!data) {
    return (
      <PageMessage>
        Order data is not available.
      </PageMessage>
    );
  }
const refreshOrder = async () => {
  try {
    const refreshed =
      await OrderAdminService.get(parsedOrderId);

    setData(refreshed);
  } catch (err) {
    console.error(
      "Failed to refresh order",
      err,
    );

    setError(
      err instanceof Error
        ? err.message
        : "Failed to refresh order.",
    );
  }
};
  return (
    <AdminOrderForm
      orderId={parsedOrderId}
      order={{
        requestor_name: data.requestor_name,
        requestor_email: data.requestor_email,
        requestor_phone: data.requestor_phone,
        order_date: data.order_date,
        order_status: data.order_status,
        id: data.id,
        items: data.items,
      }}
      orderItems={data.items ?? []}
      invoice={data.invoice ?? null}

      invoiceCommercials={data.invoice_commercial ?? null}
      paymentSchedules={data.payment_schedules ?? []}
      onOrderChange={(order: UserOrderResponse | null) =>
        setData(current =>
          current
            ? { ...current, ...order }
            : current
        )
      }
      onOrderItemsSaved={refreshOrder}
      onInvoiceChange={(invoice: InvoiceResponse) =>
        setData(current =>
          current
            ? { ...current, invoice }
            : current
        )
      }
      onInvoiceCommercialsSaved={refreshOrder}
      onPaymentSchedulesChange={(schedules: PaymentScheduleResponse[]) =>
        setData(current =>
          current
            ? {
                ...current,
                payment_schedules: schedules,
              }
            : current
        )
      }
    />
  );
}